'use strict';

/* ═══════════════════════════════════════════════════════════
   Favourites — localStorage-backed product wishlist.

   Storage: browser-only for now (key `babuco_favourites`, matching
   the `babuco_` prefix convention used by the infinite-scroll toggle
   in premium-ux.js). `save()` is the single choke point every write
   passes through — that's deliberately where account-sync (a
   customer metafield, via an App Proxy) gets added later without
   touching any of the call sites below.

   Each saved entry is a small snapshot {id, title, url, image, price}
   captured from the heart button's own data attributes at the moment
   it's favourited (see card-product.liquid / buy-buttons.liquid),
   not just a bare id — this lets the favourites page
   (main-favourites.liquid) render straight out of localStorage with
   no product fetch of its own. Prices can go stale between visits;
   the page always links through to the live product for the real
   current price at checkout.

   Style matches the house pattern (card-hover-gallery.js,
   premium-ux.js): 'use strict' + one IIFE, [data-*] hooks, a
   window.Babuco* re-init/API surface.

   Cards are replaced wholesale by facets (facets.js) and appended by
   Load More (premium-ux.js initLoadMore) — so, exactly like
   card-hover-gallery.js's click interceptor, this uses a single
   delegated document-level listener rather than per-card binding,
   and exposes syncAll() for callers to re-paint state onto
   freshly-inserted DOM.
   ═══════════════════════════════════════════════════════════ */
(function () {
  var STORAGE_KEY = 'babuco_favourites';

  function load() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  }

  function save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // Safari private mode / storage disabled — favourites just won't persist.
    }
    syncAll();
    updateHeaderCount();
    document.dispatchEvent(new CustomEvent('babuco:favourites-change', { detail: { items: items } }));
  }

  function getAll() {
    return load();
  }

  function has(id) {
    id = String(id);
    return load().some(function (item) { return item.id === id; });
  }

  function readSnapshot(btn) {
    return {
      id: String(btn.getAttribute('data-fav-id') || ''),
      title: btn.getAttribute('data-fav-title') || '',
      url: btn.getAttribute('data-fav-url') || '',
      image: btn.getAttribute('data-fav-image') || '',
      price: btn.getAttribute('data-fav-price') || '',
    };
  }

  // Toggles by id; when adding (not removing), pass the button so its
  // data attributes can be captured as the stored snapshot.
  function toggle(id, btn) {
    id = String(id);
    var items = load();
    var index = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === id) { index = i; break; }
    }
    var justAdded;
    if (index === -1) {
      items.push(btn ? readSnapshot(btn) : { id: id });
      justAdded = true;
    } else {
      items.splice(index, 1);
      justAdded = false;
    }
    save(items);
    return justAdded;
  }

  function paintButton(btn, isFav) {
    btn.classList.toggle('is-active', isFav);
    btn.setAttribute('aria-pressed', isFav ? 'true' : 'false');
    btn.setAttribute('aria-label', isFav ? 'Remove from favourites' : 'Add to favourites');
  }

  function syncAll() {
    var items = load();
    document.querySelectorAll('[data-fav-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-fav-id');
      if (!id) return;
      paintButton(btn, items.some(function (item) { return item.id === id; }));
    });
  }

  function updateHeaderCount() {
    var count = load().length;
    document.querySelectorAll('[data-fav-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fav-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var id = btn.getAttribute('data-fav-id');
    if (!id) return;
    toggle(id, btn);
  });

  document.addEventListener('DOMContentLoaded', function () {
    syncAll();
    updateHeaderCount();

    // Safety net for insertion paths that don't already call syncAll()
    // explicitly (facets.js and premium-ux.js's Load More both do) — e.g.
    // product recommendations, which arrive via their own async fetch.
    if ('MutationObserver' in window) {
      var pending = null;
      var observer = new MutationObserver(function (mutations) {
        var hasNewHearts = mutations.some(function (m) {
          return Array.prototype.some.call(m.addedNodes, function (node) {
            if (node.nodeType !== 1) return false;
            return node.matches('[data-fav-toggle]') || !!node.querySelector('[data-fav-toggle]');
          });
        });
        if (!hasNewHearts) return;
        clearTimeout(pending);
        pending = setTimeout(syncAll, 50);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  });

  window.BabucoFavourites = {
    toggle: toggle,
    has: has,
    getAll: getAll,
    syncAll: syncAll,
  };
})();
